import { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import PageMeta from '../../../shared/PageMeta';
import { useAuth } from '../../../context/AuthContext';
import { useOmsOverview } from './hooks/useOmsOverview';
import { useScope } from './hooks/useScope';
import { usePerformanceData } from './hooks/usePerformanceData';
import CommandDeck from './components/CommandDeck';
import ScopeBar from './components/ScopeBar';
import TodayFocus from './components/TodayFocus';
import PerformanceExplorer from './components/PerformanceExplorer';
import MomentumBoard from './components/MomentumBoard';
import PipelineStrip from './components/PipelineStrip';
import ApprovalsStrip from './components/ApprovalsStrip';
import DailyReportButton from './components/DailyReportButton';
import DrillDownDrawer from './components/DrillDownDrawer';
import EngineerDetailDrawer from './components/EngineerDetailDrawer';
import { deriveExecutiveInsights } from './utils/deriveCommand';
import { deltaPhrase } from './utils/scopeWindow';
import type { EngineerPerf } from './utils/performanceModel';
import type { ReportPayload } from './utils/exportReport';
import type { OmsDrillBucket } from './types';

/**
 * OMS Operations Dashboard — unified Manager + Head. Architecture answers
 * the manager's questions in priority order: the Command Deck says how the
 * portfolio feels this morning, Today's Focus says what to act on, and the
 * Performance Explorer (the centerpiece) answers who is performing well and
 * who is not — location → project → engineer, for any time window, with
 * the proof exportable from the scope bar. Head-only approvals render
 * purely off payload presence.
 */
export default function OmsOperationsDashboardPage() {
  const navigate = useNavigate();
  const { role, user, activeDepartment, rolesByDepartment } = useAuth();
  // Managers and Heads get the date-scoped Daily Report in place of the
  // WYSIWYG export (managers scoped to their projects, heads to the whole
  // active portfolio — the backend decides from the JWT); any other role
  // keeps the original ExportMenu.
  //
  // Resolve the role the same way Home.tsx routes this page: the persisted
  // `role` string can be empty on older sessions even when the user IS a
  // Manager/Head, which would otherwise wrongly fall back to the old export.
  const effectiveRole = (() => {
    const trimmed = (role || '').trim();
    if (trimmed) return trimmed;
    const fromUser =
      (user && typeof user.role === 'object' && user.role?.name) || user?.roleName || '';
    if (fromUser) return String(fromUser);
    const deptEntry = rolesByDepartment?.[activeDepartment];
    if (deptEntry && typeof deptEntry === 'object' && 'name' in deptEntry) {
      return String((deptEntry as { name?: string }).name ?? '');
    }
    return '';
  })();
  const showDailyReport = /manager|head/i.test(effectiveRole);
  const [scope, actions] = useScope();
  const [drillBucket, setDrillBucket] = useState<OmsDrillBucket | null>(null);
  const [openEngineer, setOpenEngineer] = useState<EngineerPerf | null>(null);

  // Portfolio-state slices (map, pipeline, approvals, insights) are
  // point-in-time; the Explorer + Momentum obey the scope bar's window.
  const { data, isLoading, isError, error } = useOmsOverview('all');
  const perf = usePerformanceData(scope, data?.map ?? null);

  const insights = useMemo(() => deriveExecutiveInsights(data), [data]);
  const userName = (user?.name as string | undefined) ?? '';
  const handleOpenProject = (projectId: string) => navigate(`/projects/view/${projectId}`);

  // WYSIWYG export: the Explorer keeps this ref pointed at exactly what it
  // is showing; the scope-bar download reads it at click time.
  const reportRef = useRef<ReportPayload | null>(null);
  const handleReport = useCallback((payload: ReportPayload) => {
    reportRef.current = payload;
  }, []);

  const openEngineerRow = useMemo(
    () => perf.current?.resources.find((r) => r.resourceId === openEngineer?.id) ?? null,
    [perf, openEngineer]
  );

  return (
    <>
      <PageMeta
        title="OMS Command Center - Sorigin FMS-OMS"
        description="Unified OMS operations command center for Manager and Head roles"
      />
      <div className="min-h-screen space-y-4 pb-24">
        <CommandDeck
          data={data ?? null}
          loading={isLoading}
          userName={userName}
          onFocusState={actions.drillToState}
        />

        {isError && (
          <div className="flex items-center gap-3 rounded-xl border border-[#9F1239]/30 bg-[#9F1239]/5 px-4 py-3">
            <AlertTriangle className="h-5 w-5 shrink-0 text-[#9F1239]" />
            <div className="text-sm">
              <p className="font-semibold text-[#9F1239]">Error loading dashboard</p>
              <p className="text-[#9F1239]/80">{error instanceof Error ? error.message : 'Unknown error'}</p>
            </div>
          </div>
        )}

        <ScopeBar
          scope={scope}
          actions={actions}
          model={perf.model}
          refreshing={perf.isFetching}
          getReport={() => reportRef.current}
          exportSlot={showDailyReport ? <DailyReportButton /> : undefined}
        />

        <TodayFocus
          insights={insights}
          loading={isLoading}
          onOpenProject={handleOpenProject}
          onDrill={setDrillBucket}
          onFindInExplorer={actions.setQuery}
        />

        {data?.approvals && <ApprovalsStrip approvals={data.approvals} />}

        <PerformanceExplorer
          scope={scope}
          actions={actions}
          model={perf.model}
          current={perf.current ?? null}
          previous={perf.previous ?? null}
          loading={perf.isLoading}
          onOpenEngineer={setOpenEngineer}
          onOpenProject={handleOpenProject}
          onReport={handleReport}
        />

        <MomentumBoard scope={scope} model={perf.model} />

        <PipelineStrip
          layered={data?.kpis ?? null}
          loading={isLoading}
          onSelect={setDrillBucket}
        />
      </div>

      <EngineerDetailDrawer
        engineer={openEngineer}
        row={openEngineerRow}
        window={scope.window}
        deltaPhrase={deltaPhrase(scope.preset)}
        onClose={() => setOpenEngineer(null)}
      />

      <DrillDownDrawer
        bucket={drillBucket}
        onClose={() => setDrillBucket(null)}
        onOpenProject={handleOpenProject}
      />
    </>
  );
}
